import nookies from "nookies";
import styled from "styled-components";
import { Tabs, Tab, Panel, Box } from "@bca/ui-library";
import jwt_decode from "jwt-decode";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

import SalesWithFetch from "../components/sales/SalesWithFetch";
import { TabButton } from "../components/sales/TabButtons";
import TabContent from "../components/sales/TabContent";
import SalesPageLoading from "../components/sales/SalesPageLoading";
import SalesPageError from "../components/sales/SalesPageError";
import {
  SalesPageLayout,
  SalesPageTitle,
} from "../components/sales/SalesPageLayout";
import SalesResults from "../components/sales/SalesResults";
import SalesResultsContainer from "../components/sales/SalesResultsContainer";
import getToken from "../components/sso/getToken";
import {
  SET_IS_LOL_VENDOR,
  SET_IS_TO_USE_AUTH_ZERO,
  SET_IS_VENDOR,
} from "../redux/slices/appSlice";
import pageViewDataPush from "../src/AdobeDataPushes/pageViewDataPush";
import DecodedToken from "../src/Interfaces/DecodedToken";
import getEnvironment from "../components/ApiCalls/getEnvironment";
import { redirectToIdp } from "../components/sso/redirectToIdp";
import { useGetSales } from "../components/Hooks/useGetSales.hook";
import { getDateForAPI } from "../components/utils/getDateForAPI";
import {
  FeatureFlagManager,
  FeatureFlagProvider,
} from "bca-feature-flag-provider";
import { FEATURE_FLAGS } from "../src/constants/featureFlags";
import getAccountInfo, { AccountInfo } from "../src/api/getAccountInfo";
import addLog from "../components/utils/addLog";
import getLolAccount from "../src/api/getLolAccount";
import { useAuth } from "@bca-auth0/bca-auth0-provider";
import { useGetLolAccount } from "../components/Hooks/useGetLolAccount.hook";
import getBuyerDomain from "../components/ApiCalls/getBuyerDomain";
import ErrorPage from "../components/common/ErrorPage/ErrorPage";
import RedirectPage from "../components/common/RedirectPage";
import { StoreType } from "../redux/store.types";

const UsabillaContainer = styled.div`
  padding-top: 14px;
  padding-bottom: 40px;
`;

const Sales: React.FunctionComponent = ({
  activeTab,
  isVendor: isIDPVendor,
  isLolVendor,
  user: serverTokenUser,
  idToken: serverIdToken,
  isToUseAuthZero,
  appConfigurationUrl,
  authLogoutUrl,
}: any) => {
  const dispatch = useDispatch();
  const [isInitalized, setIsInitialized] = useState<boolean>(false);

  const store = useSelector(
    (state: StoreType) => ({
      isToUseAuthZero: state.app.isToUseAuthZero,
    }),
    shallowEqual
  );

  const {
    isAuthenticated,
    authUser,
    error: authError,
    idToken: authIdToken,
  } = useAuth(!isToUseAuthZero);

  const {
    isLolVendor: auth0LolVendor,
    isLoading: isLolVendorLoading,
  } = useGetLolAccount(isToUseAuthZero);

  const user = isToUseAuthZero && isAuthenticated ? authUser : serverTokenUser;
  const idToken =
    isToUseAuthZero && isAuthenticated ? authIdToken : serverIdToken;
  const isVendor =
    isToUseAuthZero && isAuthenticated ? authUser.isVendor : isIDPVendor;

  const resolvedIsLolVendor = isToUseAuthZero ? auth0LolVendor : isLolVendor;

  const initialLogic = () => {
    dispatch(SET_IS_VENDOR(isVendor));
    dispatch(SET_IS_LOL_VENDOR(resolvedIsLolVendor));
    if (user?.peep_analyticsid && user?.ssoId && user?.casId && !isInitalized) {
      pageViewDataPush(user);
      setIsInitialized(true);
    }
  };

  useEffect(initialLogic, [user, resolvedIsLolVendor]);

  useEffect(() => {
    dispatch(SET_IS_TO_USE_AUTH_ZERO(isToUseAuthZero));
  }, [isToUseAuthZero, dispatch]);

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const week: Date[] = [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const date = new Date();
    date.setDate(today.getDate() + i);
    return date;
  });

  const days = `${getDateForAPI(today)},${getDateForAPI(tomorrow)}`;
  const { data, error } = useGetSales(days, store.isToUseAuthZero);

  if (isToUseAuthZero && !isAuthenticated && !authError) {
    return (
      <RedirectPage
        week={week}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
      />
    );
  }

  if (isToUseAuthZero && isAuthenticated && isLolVendorLoading) {
    return (
      <SalesPageLoading
        user={user}
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
        week={week}
      ></SalesPageLoading>
    );
  }

  if (isToUseAuthZero && authError) {
    return (
      <ErrorPage
        user={user}
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
      />
    );
  }

  if (error) {
    return (
      <SalesPageError
        user={user}
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
        week={week}
        activeTabIndex={0}
      ></SalesPageError>
    );
  }

  if (!data) {
    return (
      <SalesPageLoading
        user={user}
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
        week={week}
      ></SalesPageLoading>
    );
  }

  const { dayProgrammes } = data;
  const [todaysSales, tomorrowsSales] = dayProgrammes;

  const dayTotals = dayProgrammes.map((day) => {
    return {
      lotsCount: day.lotsAvailable,
      salesCount: day.salesAvailable,
    };
  });

  const getSaleCount = (index: number) => {
    return dayTotals[index].salesCount;
  };

  const getLotsCount = (index: number) => {
    return dayTotals[index].lotsCount;
  };

  const getActiveTabIndex = (dayTotals, activeTab: string | null) => {
    const salesCount = dayTotals.map((day) => day.salesCount);
    const hasSales = (dailySales: number) => dailySales > 0;

    const activeTabIndex = salesCount.findIndex(hasSales);
    if (activeTabIndex === -1) {
      return 0;
    }

    if (activeTab === "tomorrow") {
      return 1;
    }

    return activeTabIndex;
  };

  return (
    <FeatureFlagProvider
      clientSecret={appConfigurationUrl}
      keys={FEATURE_FLAGS}
      context={{ userId: user.customerNumber, groups: [user.cardColour] }}
    >
      <SalesPageLayout
        user={user ? user : undefined}
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
      >
        <Tabs activeTabIndex={getActiveTabIndex(dayTotals, activeTab)}>
          <div
            style={{
              backgroundImage:
                "linear-gradient(to bottom, rgba(7, 11, 22, 0) 35%, rgba(7, 11, 22, 0) 130%), linear-gradient(to bottom, #091535, #091535)",
            }}
          >
            <SalesPageTitle></SalesPageTitle>
            <Box
              maxWidth={["100%", "calc(100% - 48px)", "976px", "976px"]}
              style={{
                overflow: "auto",
                whiteSpace: "nowrap",
                backgroundImage:
                  "linear-gradient(to bottom, rgba(7, 11, 22, 0) 35%, rgba(7, 11, 22, 0) 130%), linear-gradient(to bottom, #091535, #091535)",
                paddingLeft: "0",
                margin: "0 auto",
                height: "73px",
              }}
            >
              {week.map((day, index) => {
                return (
                  <Tab key={index}>
                    <TabButton>
                      <TabContent
                        day={day}
                        index={index}
                        lotsCount={getLotsCount(index)}
                        saleCount={getSaleCount(index)}
                      ></TabContent>
                    </TabButton>
                  </Tab>
                );
              })}
            </Box>
          </div>
          <Panel>
            <SalesResultsContainer>
              <SalesResults items={todaysSales.sales} />
            </SalesResultsContainer>
          </Panel>
          <Panel>
            <SalesResultsContainer>
              <SalesResults items={tomorrowsSales.sales} />
            </SalesResultsContainer>
          </Panel>
          {week.slice(2).map((day: Date, index: number) => (
            <Panel key={index}>
              <SalesWithFetch day={day} />
            </Panel>
          ))}
        </Tabs>
        <UsabillaContainer>
          <div
            style={{
              width: "100%",
              maxWidth: 1080,
              margin: "0 auto",
            }}
            // eslint-disable-next-line
            ub-in-page="5fa291e7a7f8640d5f51167a"
          />
        </UsabillaContainer>
      </SalesPageLayout>
    </FeatureFlagProvider>
  );
};

export async function getServerSideProps(context) {
  const featureFlagManager = new FeatureFlagManager();
  await featureFlagManager.initializeFeatureManager(
    process.env.APP_CONFIGURATION_URL
  );
  const useAuthZeroResult =
    await featureFlagManager.getFeatureFlagStatus("authzero");

  const isToUseAuthZero =
    !useAuthZeroResult.isError && useAuthZeroResult.isEnabled;

  if (isToUseAuthZero) {
    let props: any = {};
    props.appConfigurationUrl = process.env.APP_CONFIGURATION_URL;
    props.auth0Domain = process.env.AUTH0_DOMAIN;
    props.auth0ClientId = process.env.AUTH0_CLIENT_ID;
    props.auth0Audience = process.env.AUTH0_AUDIENCE;
    props.auth0Scope = process.env.AUTH0_SCOPE;
    props.redirectUri = process.env.AUTH_PROVIDER_REDIRECT_URI;
    props.isToUseAuthZero = isToUseAuthZero;
    const buyerAPIDomain = getBuyerDomain(isToUseAuthZero);
    props.buyerAPIDomain = buyerAPIDomain ? buyerAPIDomain.href : "";
    props.authLogoutUrl = process.env.AUTH0_LOGOUT_URL;

    return {
      props,
    };
  } else {
    // in prod redirecting to sso
    redirectToIdp(
      context,
      getEnvironment(),
      process.env.VIRTUAL_DOMAIN,
      process.env.URL_SUFFIX
    );

    let firstName = undefined;
    let lastName = undefined;
    let peep_analyticsid = "";
    let ssoId = "";
    let casId = "";
    let customerNumber = "";
    let accountInfo: AccountInfo = null;

    let props: any = {};
    const activeTab = context?.query?.tab?.toLowerCase() || null;

    if (nookies.get(context).FullName) {
      props.username = nookies
        .get(context)
        .FullName.split("&")[0]
        .split("=")[1];
    }

    if (activeTab === "tomorrow") {
      props.activeTab = activeTab;
    }

    const authToken = getToken(nookies.get(context));
    const idToken = nookies.get(context).idtoken || "";

    let isVendor = false;

    if (authToken) {
      const decodedToken = jwt_decode(authToken) as DecodedToken;
      peep_analyticsid = decodedToken.peep_analyticsid ?? "";
      firstName = decodedToken.user_name.split(" ")[0];
      lastName = decodedToken.user_name.split(" ")[1];
      ssoId = decodedToken.sub;
      casId = decodedToken.sourceaccount_id ?? "";
      isVendor = decodedToken.is_vendor;
      customerNumber = decodedToken.customer_number;
    }

    try {
      accountInfo = await getAccountInfo(authToken);
    } catch (error) {
      addLog({ message: error.message, error });
    }

    try {
      const lolAccount = await getLolAccount(authToken);
      props.isLolVendor = lolAccount.isLiveOnlineVendor;
    } catch (error) {
      addLog({ message: error.message, error });
    }

    const { cardColour = "" } = accountInfo ?? {};

    props.user = {
      firstName,
      lastName,
      peep_analyticsid,
      ssoId,
      casId,
      customerNumber,
      cardColour,
    };

    props.isVendor = isVendor;
    props.idToken = idToken;
    props.appConfigurationUrl = process.env.APP_CONFIGURATION_URL;
    props.isToUseAuthZero = isToUseAuthZero;

    return {
      props,
    };
  }
}
export default Sales;
